import { showConfirm } from '../components/ConfirmDialog';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfig, useConfigLoadError } from '../hooks/useConfig';
import { useMusicians } from '../hooks/useMusicians';
import { useCrew } from '../hooks/useCrew';
import { useWriteGate } from '../lib/writeGate';
import { useRackUsage } from '../hooks/useRackUsage';
import { rackCapacities, isChannelTaken } from '../lib/rackUsage';
import { mergeStageRacks, type SoundConfig, type TransportType } from '../types';
import ConfigPageHeader from '../components/ConfigPageHeader';
import ConfigField from '../components/ConfigField';
import AutoTextarea from '../components/AutoTextarea';
import ProviderSelector from '../components/ProviderSelector';
import { useFocusField } from '../hooks/useFocusField';
import { useLayoutOrder } from '../hooks/useLayoutOrder';
import { sortByLayoutOrder } from '../lib/layoutOrder';
import PageLoadError from '../components/PageLoadError';

export default function ConfigSoundPage() {
  const { config, updateSound, updateGeneral, reload } = useConfig();
  const { musicians } = useMusicians();
  const { crew } = useCrew();
  // Lot 3a — the rider_config could not be read: the page would render EMPTY
  // fields as if the band had nothing typed. See PageLoadError.tsx.
  const configLoad = useConfigLoadError();

  // Capability-aware write gate (lot A2-CAP, 2026-09-25). Every write of this
  // page persists through `useConfig` -> PUT /api/bands/:id/rider/config, the
  // rider CONTENT: a globally `read-only` account keeps it ONLY through
  // `content.rider` (conditional pre-guard mount, server/index.js). NEVER the
  // blanket `canWrite` here — it would freeze the very crew member the
  // capability is for.
  const writeGate = useWriteGate();
  const canEditContent = writeGate.canWriteCap('content.rider');
  // The note describes the page's REAL state: it must NOT tell a globally
  // read-only account that editing is disabled while the capability that
  // unlocks THIS page is held (the fields are open). Show it only when the
  // page's own write predicate is denied.
  const readOnlyNote = writeGate.readOnlyReason === 'global-read-only' && !canEditContent;
  const { t } = useTranslation();
  const { usage } = useRackUsage();
  const [transport, setTransport] = useState<TransportType>('van');
  useFocusField(true);
  // Reorder the Sound fields to mirror the Rider Layout's `sound.*` order.
  const layoutOrder = useLayoutOrder('sound');

  const cfg = config.sound[transport];
  const cfgGeneral = config.general[transport];

  const handleChange = (key: keyof SoundConfig, value: string | number) => {
    updateSound({
      [transport]: { ...cfg, [key]: value },
    } as any);
  };

  // Sound Limit lives on the General config but is EDITED on the Sound page
  // (moved there 2026-08-08) — route its writes to updateGeneral.
  const handleSoundLimit = (value: string) => {
    updateGeneral({
      [transport]: { ...config.general[transport], soundLimitDb: value },
    } as any);
  };

  // Page-level populate: ONE confirmation, then copy the entire section
  // from `src` into the current mode (overwrite everything).
  const populateAll = async (src: TransportType) => {
    const srcCfg = config.sound[src];
    if (!(await showConfirm(t('configSound.populateAllConfirm', { src })))) return;
    updateSound({ [transport]: { ...srcCfg } } as any);
  };

  // Per-field populate: confirm then copy this one field from `src`.
  const populateField = (key: keyof SoundConfig) => async (src: TransportType) => {
    const srcVal = config.sound[src][key];
    if (srcVal === cfg[key]) return;
    if (srcVal === undefined) return; // optional field absent in the source preset
    if (await showConfirm(`Overwrite "${key}" with the value from ${src}?`)) {
      handleChange(key, srcVal);
    }
  };

  // Populate du champ COMPOSÉ « PA mix output » (rack + paire) : copie les 2 clés.
  const populatePaMix = async (src: TransportType) => {
    const srcCfg = config.sound[src];
    if (!srcCfg.paMixRackId && !srcCfg.paMixOutStart) return;
    if (!(await showConfirm(t('configSound.populateFieldConfirm', { key: 'PA mix output', src })))) return;
    updateSound({ [transport]: { ...cfg, paMixRackId: srcCfg.paMixRackId, paMixOutStart: srcCfg.paMixOutStart } } as any);
  };

  // Each Rider Layout field (`sound.*`) is its own reorderable block, so
  // reordering in the Layout Editor is reflected here. Blocks whose id is not
  // in the layout keep their default relative order (via sortByLayoutOrder).
  const blocks = [
    // NOTE (v0.591): the technical intro moved to its own TECHNICAL RIDER rider
    // section and is now edited on the GENERAL config page (band-level layout
    // text, like the legal intro) — it is no longer a Sound/SoundConfig field.
    {
      id: 'sound.limit',
      render: () => (
        <div className="form-section" data-field-id="sound.limit">
          <h2>{t('configSound.soundLimits')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configSound.soundLimitLabel')} current={transport} fieldId="sound.limit" exampleText="100 dB LeqA 60min / 125 dB LeqC" onUseExample={handleSoundLimit} onPopulateFrom={async (src) => {
            const srcVal = config.general[src].soundLimitDb;
            if (srcVal === cfgGeneral.soundLimitDb) return;
            if (await showConfirm(`Overwrite "soundLimitDb" with the value from ${src}?`)) {
              handleSoundLimit(srcVal);
            }
          }}>
            <AutoTextarea disabled={!canEditContent}
              value={cfgGeneral.soundLimitDb || ''}
              onChange={(e) => handleSoundLimit(e.target.value)}
              placeholder={t('configSound.soundLimitPh')}
            />
          </ConfigField>
        </div>
      ),
    },
    {
      id: 'sound.foh',
      render: () => (
        <div className="form-section" data-field-id="sound.foh">
          <h2>{t('configSound.fohConsole')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configSound.fohConsole')} current={transport} fieldId="sound.foh" exampleText="Behringer Wing Rack + DL8" onUseExample={(t) => handleChange('fohConsole', t)} onPopulateFrom={populateField('fohConsole')}>
            <AutoTextarea disabled={!canEditContent}
              value={cfg.fohConsole}
              onChange={(e) => handleChange('fohConsole', e.target.value)}
              placeholder="e.g. Behringer Wing Rack + DL8"
            />
          </ConfigField>
          <ProviderSelector disabled={!canEditContent} value={cfg.fohProvider || 'venue'} onChange={(v) => handleChange('fohProvider', v)} />
        </div>
      ),
    },
    {
      id: 'sound.foh-requirements',
      render: () => (
        <div className="form-section" data-field-id="sound.foh-requirements">
          <h2>{t('configSound.fohRequirements')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configSound.fohRequirements')} current={transport} fieldId="sound.foh-requirements" exampleText={'e.g. stable table or riser min 1 m x 1 m at the FOH position\n2x 230 V sockets\nchair'} onUseExample={(t) => handleChange('fohRequirements', t)} onPopulateFrom={populateField('fohRequirements')}>
            <AutoTextarea disabled={!canEditContent}
              value={cfg.fohRequirements || ''}
              onChange={(e) => handleChange('fohRequirements', e.target.value)}
              placeholder={'e.g. stable table or riser min 1 m x 1 m at the FOH position\n2x 230 V sockets\nchair'}
            />
          </ConfigField>
          {/* VENUE HIGHLIGHT (2026-10-18): requirements are venue requests by default. */}
          <ProviderSelector disabled={!canEditContent} value={cfg.fohRequirementsProvider || 'venue'} onChange={(v) => handleChange('fohRequirementsProvider', v)} />
        </div>
      ),
    },
    {
      id: 'sound.pa-mix-output',
      render: () => {
        // Sortie du MIX PRINCIPAL vers la sono : choisie PARMI LES RACKS/TABLES
        // déclarés dans la config (ex. Wing Rack 1-2) — user 2026-09-19.
        // Paire stéréo : on offre des blocs de 2 canaux contigus, grisés par
        // l'occupation (mêmes règles que les sorties IEM/wedges/sidefills).
        const racks = mergeStageRacks(config.globalRacks, config.specs, transport);
        const rack = racks.find((r) => r.id === cfg.paMixRackId);
        const { outputs } = rackCapacities(rack);
        const ch = cfg.paMixOutStart || 0;
        const occAt = (c: number) => isChannelTaken(usage, 'outputs', cfg.paMixRackId || '', c, cfg.paMixRackId, ch);
        const pairs: { ch: number; blocked: boolean; label: string }[] = [];
        for (let c = 1; c <= outputs; c++) {
          const end = c + 1;
          const isCurrent = c === ch;
          const runsPast = end > outputs;
          const occ = isCurrent ? undefined : (occAt(c) || occAt(end));
          pairs.push({
            ch: c,
            blocked: runsPast || (!!occ && !isCurrent),
            label: runsPast ? `${c} (needs ${end})` : `${c}-${end}${occ ? ` (${occ})` : ''}`,
          });
        }
        return (
          <div className="form-section" data-field-id="sound.pa-mix-output">
            <h2>{t('configSound.paMixOutputLabel')}</h2>
            <ConfigField disabled={!canEditContent} label={t('configSound.paMixOutputLabel')} current={transport} fieldId="sound.pa-mix-output" exampleText={t('configSound.paMixOutputHint')} onPopulateFrom={populatePaMix}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <select disabled={!canEditContent}
                  className="form-input"
                  style={{ width: 220 }}
                  value={cfg.paMixRackId || ''}
                  onChange={(e) => handleChange('paMixRackId', e.target.value)}
                  title={t('configSound.paMixRack')}
                >
                  <option value="">{t('configSound.paMixNoRack')}</option>
                  {racks.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
                <select
                  className="form-input"
                  style={{ width: 180 }}
                  value={ch}
                  disabled={!canEditContent || !cfg.paMixRackId}
                  onChange={(e) => handleChange('paMixOutStart', parseInt(e.target.value) || 0)}
                  title={t('configSound.paMixChannel')}
                >
                  <option value={0}>—</option>
                  {pairs.map((o) => (
                    <option key={o.ch} value={o.ch} disabled={o.blocked}>{o.label}</option>
                  ))}
                </select>
                <span className="empty-hint">{cfg.paMixRackId && ch ? t('configSound.paMixPreview', { rack: rack?.name || '', from: ch, to: ch + 1 }) : ''}</span>
              </div>
            </ConfigField>
            <p className="empty-hint">{t('configSound.paMixOutputHint')}</p>
          </div>
        );
      },
    },
    {
      id: 'sound.pa',
      render: () => (
        <div className="form-section" data-field-id="sound.pa">
          <h2>{t('configSound.paSystem')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configSound.paSystem')} current={transport} fieldId="sound.pa" exampleText="D&B / L-Acoustics / Meyer Sound" onUseExample={(t) => handleChange('paSystem', t)} onPopulateFrom={populateField('paSystem')}>
            <AutoTextarea disabled={!canEditContent}
              value={cfg.paSystem}
              onChange={(e) => handleChange('paSystem', e.target.value)}
              placeholder="e.g. D&B / L-Acoustics / Meyer Sound"
            />
          </ConfigField>
          <ProviderSelector disabled={!canEditContent} value={cfg.paProvider || 'venue'} onChange={(v) => handleChange('paProvider', v)} />
        </div>
      ),
    },
    {
      id: 'sound.monitor-console',
      render: () => (
        <div className="form-section" data-field-id="sound.monitor-console">
          <h2>{t('configSound.monitorConsole')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configSound.monitorConsole')} current={transport} fieldId="sound.monitor-console" exampleText="e.g. Band brings its own monitor console (Midas M32 / DiGiCo SD)" onUseExample={(t) => handleChange('monitorConsole', t)} onPopulateFrom={populateField('monitorConsole')}>
            <AutoTextarea disabled={!canEditContent}
              value={cfg.monitorConsole || ''}
              onChange={(e) => handleChange('monitorConsole', e.target.value)}
              placeholder="e.g. Band brings its own monitor console (Midas M32 / DiGiCo SD)"
            />
          </ConfigField>
          <ProviderSelector disabled={!canEditContent} value={cfg.monitorConsoleProvider || 'venue'} onChange={(v) => handleChange('monitorConsoleProvider', v)} />
        </div>
      ),
    },
    {
      id: 'sound.monitor-requirements',
      render: () => (
        <div className="form-section" data-field-id="sound.monitor-requirements">
          <h2>{t('configSound.monitorRequirements')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configSound.monitorRequirements')} current={transport} fieldId="sound.monitor-requirements" exampleText={'e.g. stable riser for the monitor console\n2x 230 V sockets\nchair'} onUseExample={(t) => handleChange('monitorRequirements', t)} onPopulateFrom={populateField('monitorRequirements')}>
            <AutoTextarea disabled={!canEditContent}
              value={cfg.monitorRequirements || ''}
              onChange={(e) => handleChange('monitorRequirements', e.target.value)}
              placeholder={'e.g. stable riser for the monitor console\n2x 230 V sockets\nchair'}
            />
          </ConfigField>
          <ProviderSelector disabled={!canEditContent} value={cfg.monitorRequirementsProvider || 'venue'} onChange={(v) => handleChange('monitorRequirementsProvider', v)} />
        </div>
      ),
    },
    {
      id: 'sound.monitor',
      render: () => (
        <div className="form-section" data-field-id="sound.monitor">
          <h2>{t('configSound.monitorSystem')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configSound.monitorSystem')} current={transport} fieldId="sound.monitor" exampleText="Own monitor mixer + 2x wedges + 1x 18in subwoofer" onUseExample={(t) => handleChange('monitorSystem', t)} onPopulateFrom={populateField('monitorSystem')}>
            <AutoTextarea disabled={!canEditContent}
              value={cfg.monitorSystem}
              onChange={(e) => handleChange('monitorSystem', e.target.value)}
              placeholder="e.g. Own monitor mixer + 2x wedges + 1x 18in subwoofer"
            />
          </ConfigField>
          <ProviderSelector disabled={!canEditContent} value={cfg.monitorProvider || 'venue'} onChange={(v) => handleChange('monitorProvider', v)} />
          {/* RÉSUMÉ LECTURE SEULE (2026-10-17, user : « un résumé des retours sous monitor
              system, juste pour visualiser ») — les retours (wedges) et le subwoofer des
              personnes SÉLECTIONNÉES (cartes), sans rien éditer ici. */}
          {(() => {
            const sel = [
              ...(musicians || []).filter((m) => config.rider?.activeMusicianIds?.includes(m.id)),
              ...(crew || []).filter((c) => config.rider?.activeCrewIds?.includes(c.id)),
            ].filter((m) => (m.wedges || 0) > 0 || m.subwoofer);
            if (sel.length === 0) return null;
            return (
              <div style={{ marginTop: 10 }}>
                <label style={{ fontSize: '0.8rem', color: 'var(--text-dim)', display: 'block', marginBottom: 6 }}>
                  {t('configSound.returnsSummary')}
                </label>
                <div className="backline-list">
                  {sel.map((m) => (
                    <div key={m.id} className="backline-row" style={{ gridTemplateColumns: 'minmax(0,1fr) 180px' }}>
                      <span className="backline-label" style={{ display: 'inline-flex', alignItems: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', minHeight: 34, padding: '4px 8px' }}>{m.name || '—'}</span>
                      <span className="backline-type" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', height: 34, padding: '0 8px' }}>
                        {(m.wedges || 0) > 0 && <span>{t('configSound.wedgesCount', { count: m.wedges })}</span>}
                        {m.subwoofer && <span>· Sub</span>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      ),
    },
  ];

  const sortedBlocks = sortByLayoutOrder(blocks, layoutOrder, (b) => b.id);


  // Lot 3a: the rider_config could not be read → empty fields would read as
  // "this band typed nothing". Show the shared state instead; the writes stay
  // gated by `useConfig` skipSave, so a retry cannot wipe anything.
  if (configLoad.failed) {
    return (
      <PageLoadError
        title={t('configSound.title')}
        detail={configLoad.message}
        onRetry={() => void reload().catch(() => undefined)}
      />
    );
  }

  return (
    <div className="page config-page" data-config-page="sound" data-density="socle">
      <ConfigPageHeader disabled={!canEditContent}
        iconKey="configSound.title"
        title={t('configSound.title')}
        transport={transport}
        onTransportChange={setTransport}
        onPopulateAll={populateAll}
      />

      {readOnlyNote && (
        <p className="text-dim" data-readonly-note style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 12px' }}>{t('common.readOnlyAccount')}</p>
      )}
      <div className="edit-form">
        {sortedBlocks.map((b) => b.render())}

        {/* Rehearsal & Setup was MOVED to the dedicated Schedule page (2026-08-08). */}
      </div>
    </div>
  );
}
